import { useCallback, useRef, useState } from 'react';

import {
    isCommunicationsLayerBlock,
    isLegacyScheduleBlock,
    type CommunicationBlockDefinition,
    type CommunicationBlockIdValue,
} from './communicationBlocks';
import {
    cardKey,
    type CommunicationCardKey,
    type CommunicationCardState,
} from './communicationCardState';
import { buildLegacyScheduleEmailUpdate } from './communicationsLegacyManagerEmailPersistence';
import {
    buildCommunicationBlockPayload,
    carryForwardUntouchedCommunicationBlocks,
    type CommunicationEmailCardSnapshot,
} from './communicationsPersistence';
import { CommunicationEmailAudience } from './components/CommunicationEmailCard/CommunicationEmailCard';
import { buildSwitchesSignature } from './useAutosaveCommunicationSwitches';

import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { CycleCommunicationBlockPayload } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { useError } from '@/hooks/utils/useError';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { CycleSchedule } from '@/types/cycle-evaluation/CycleSchedule';
import type { MutableRefObject } from 'react';

import { useUpdateCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import { useUpdateCycleSchedule } from '@/api/cycle-evaluation-services/cycle-schedule';
import { useAmelioTranslations } from '@/hooks';
import { buildCycleEvaluationGroupUpdatePayload } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import {
    DeadlinesSaveOutcome,
    findSaveRejection,
    getDeadlinesSaveFailureMessageKey,
    summariseDeadlinesSave,
} from '@/pages/CycleEvaluation/Deadlines/savingOutcome';

interface UseCommunicationPersistenceParams {
    group: CycleEvaluationGroup | undefined;
    cycleEvaluationGroupId: string | undefined;
    visibleBlocks: CommunicationBlockDefinition[];
    blockEnabled: Partial<Record<CommunicationBlockIdValue, boolean>>;
    cards: Partial<Record<CommunicationCardKey, CommunicationCardState>>;
    legacyScheduleQueryByEvaluationType: Partial<
        Record<EvaluationTypeValue, ReturnType<typeof useGetCycleEvaluation>>
    >;
    hydratedRef: MutableRefObject<boolean>;
    isReadOnly: boolean;
    /**
     * A launched cycle nobody has touched yet: a language switch then has no wording to put away,
     * so it writes nothing rather than re-sending every block the cycle already holds.
     */
    isUntouchedLaunchedCycle: boolean;
    defaultLanguage: string;
    updateCard: (key: CommunicationCardKey, patch: Partial<CommunicationCardState>) => void;
    handleError: ReturnType<typeof useError>['handleError'];
}

export const useCommunicationPersistence = ({
    group,
    cycleEvaluationGroupId,
    visibleBlocks,
    blockEnabled,
    cards,
    legacyScheduleQueryByEvaluationType,
    hydratedRef,
    isReadOnly,
    isUntouchedLaunchedCycle,
    defaultLanguage,
    updateCard,
    handleError,
}: UseCommunicationPersistenceParams) => {
    const { t } = useAmelioTranslations();

    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();
    const { mutateAsync: updateCycleSchedule } = useUpdateCycleSchedule();

    // Read by the switches' autosave to stand down while in flight.
    const [isPersisting, setIsPersisting] = useState(false);
    const isPersistingRef = useRef(false);
    const lastWrittenSwitchesSignatureRef = useRef<string | undefined>(undefined);

    const persistCommunications = useCallback(
        async (forceLanguage?: {
            key: CommunicationCardKey;
            language: string;
        }): Promise<boolean> => {
            if (isReadOnly) {
                return false;
            }

            if (!group || !cycleEvaluationGroupId || !hydratedRef.current) {
                return false;
            }

            isPersistingRef.current = true;
            lastWrittenSwitchesSignatureRef.current = buildSwitchesSignature({
                blockEnabled,
                cards,
            });
            setIsPersisting(true);

            try {
                const communications: CycleCommunicationBlockPayload[] = [];
                const legacyScheduleUpdates: CycleSchedule[] = [];

                const snapshotOf = (
                    key: CommunicationCardKey,
                    card: CommunicationCardState,
                ): CommunicationEmailCardSnapshot => {
                    const language =
                        forceLanguage && forceLanguage.key === key
                            ? forceLanguage.language
                            : card.language;

                    return {
                        enabled: card.enabled,
                        customizedLanguages: card.customizedLanguages,
                        seedLanguage: language,
                        titleByLanguage: card.titleByLanguage,
                        messageByLanguage: card.messageByLanguage,
                    };
                };

                for (const block of visibleBlocks) {
                    if (isLegacyScheduleBlock(block)) {
                        const managerKey = cardKey(block.id, CommunicationEmailAudience.MANAGER);
                        const employeeKey = cardKey(block.id, CommunicationEmailAudience.EMPLOYEE);
                        const managerCard = cards[managerKey];
                        const employeeCard = block.hasEmployeeCard ? cards[employeeKey] : undefined;
                        const legacySchedule =
                            legacyScheduleQueryByEvaluationType[block.evaluationType]?.data
                                ?.cycleSchedule;

                        if (
                            !managerCard ||
                            !legacySchedule ||
                            (block.hasEmployeeCard && !employeeCard)
                        ) {
                            handleError({});

                            return false;
                        }

                        const managerSnapshot = snapshotOf(managerKey, managerCard);

                        let legacyScheduleUpdate = buildLegacyScheduleEmailUpdate({
                            cycleSchedule: legacySchedule,
                            audience: CommunicationEmailAudience.MANAGER,
                            enabled: managerSnapshot.enabled,
                            customizedLanguages: managerSnapshot.customizedLanguages,
                            seedLanguage: managerSnapshot.seedLanguage,
                            titleByLanguage: managerSnapshot.titleByLanguage,
                            messageByLanguage: managerSnapshot.messageByLanguage,
                            plainTextLanguage: defaultLanguage,
                        });

                        if (employeeCard) {
                            const employeeSnapshot = snapshotOf(employeeKey, employeeCard);

                            legacyScheduleUpdate = buildLegacyScheduleEmailUpdate({
                                cycleSchedule: legacyScheduleUpdate,
                                audience: CommunicationEmailAudience.EMPLOYEE,
                                enabled: employeeSnapshot.enabled,
                                customizedLanguages: employeeSnapshot.customizedLanguages,
                                seedLanguage: employeeSnapshot.seedLanguage,
                                titleByLanguage: employeeSnapshot.titleByLanguage,
                                messageByLanguage: employeeSnapshot.messageByLanguage,
                                plainTextLanguage: defaultLanguage,
                            });
                        }

                        legacyScheduleUpdates.push(legacyScheduleUpdate);

                        continue;
                    }

                    if (!isCommunicationsLayerBlock(block)) {
                        continue;
                    }

                    const managerKey = cardKey(block.id, CommunicationEmailAudience.MANAGER);
                    const employeeKey = cardKey(block.id, CommunicationEmailAudience.EMPLOYEE);
                    const managerCard = cards[managerKey];
                    const employeeCard = block.hasEmployeeCard ? cards[employeeKey] : undefined;

                    if (!managerCard || (block.hasEmployeeCard && !employeeCard)) {
                        handleError({});

                        return false;
                    }

                    communications.push(
                        buildCommunicationBlockPayload({
                            blockId: block.id,
                            blockEnabled: blockEnabled[block.id] ?? true,
                            managerCard: snapshotOf(managerKey, managerCard),
                            employeeCard: employeeCard
                                ? snapshotOf(employeeKey, employeeCard)
                                : undefined,
                            communications: group.communications,
                        }),
                    );
                }

                communications.push(
                    ...carryForwardUntouchedCommunicationBlocks({
                        existingBlocks: group.communications,
                        writtenBlocks: communications,
                    }),
                );

                try {
                    const scheduleResults = await Promise.allSettled(
                        legacyScheduleUpdates.map((schedule) => updateCycleSchedule(schedule)),
                    );

                    // Writing `communications` on top of schedules that did not land would advance the cycle over missing data.
                    const groupResults =
                        summariseDeadlinesSave(scheduleResults) === DeadlinesSaveOutcome.SAVED
                            ? await Promise.allSettled([
                                  updateCycleEvaluationGroup({
                                      id: cycleEvaluationGroupId,
                                      payload: buildCycleEvaluationGroupUpdatePayload({
                                          group,
                                          communications,
                                      }),
                                  }),
                              ])
                            : [];

                    const results = [...scheduleResults, ...groupResults];
                    const failureMessageKey = getDeadlinesSaveFailureMessageKey(
                        summariseDeadlinesSave(results),
                    );

                    if (!failureMessageKey) {
                        return true;
                    }

                    handleError({
                        error: findSaveRejection(results),
                        data: { title: t('errors.genericTitle'), message: t(failureMessageKey) },
                    });

                    return false;
                } catch (error) {
                    handleError({ error });

                    return false;
                }
            } finally {
                isPersistingRef.current = false;
                setIsPersisting(false);
            }
        },
        [
            group,
            cycleEvaluationGroupId,
            visibleBlocks,
            blockEnabled,
            cards,
            legacyScheduleQueryByEvaluationType,
            updateCycleSchedule,
            updateCycleEvaluationGroup,
            handleError,
            isReadOnly,
            t,
        ],
    );

    const onCardLanguageChange = useCallback(
        (key: CommunicationCardKey, previousLanguage: string, nextLanguage: string) => {
            updateCard(key, { language: nextLanguage });

            if (isUntouchedLaunchedCycle) {
                return;
            }

            void persistCommunications({ key, language: previousLanguage }).catch(
                (error: unknown) => handleError({ error }),
            );
        },
        [updateCard, isUntouchedLaunchedCycle, persistCommunications, handleError],
    );

    return {
        persistCommunications,
        onCardLanguageChange,
        isPersisting,
        isPersistingRef,
        lastWrittenSwitchesSignatureRef,
    };
};
